import type { Effect, NodeRecord, ShaderDef } from '@flobi/artwork-core';

/*
 * Adjust: white balance, light, colour and texture on a picture (Tarek, 14 Oct, Canva's Adjust
 * panel as the reference). One live effect — a shader the file keeps once, with the sliders as its
 * values — so the picture underneath is never touched, every number can be changed or the whole
 * thing taken off later, and it draws the same on the canvas, in exports and in the render service
 * (the engine's shader effect). No engine change: the engine's own adjustments (hsb, colour
 * balance, sharpen) have no temperature, highlights, whites, blacks, vibrance, clarity or vignette.
 */

export const ADJUST_SHADER_ID = 'flobi-adjust-v1';

export interface AdjustControl { id: AdjustKey; label: string; min: number; max: number }
export type AdjustKey = 'temperature' | 'tint' | 'brightness' | 'contrast' | 'highlights' | 'shadows' | 'whites' | 'blacks' | 'vibrance' | 'saturation' | 'sharpness' | 'clarity' | 'vignette';

/** The panel's sections, in Canva's order. Every slider is −100…100 except Sharpness (0…100). */
export const ADJUST_SECTIONS: { id: string; label: string; icon: 'temperature' | 'light' | 'colour' | 'texture'; controls: AdjustControl[] }[] = [
  { id: 'white', label: 'White Balance', icon: 'temperature', controls: [
    { id: 'temperature', label: 'Temperature', min: -100, max: 100 },
    { id: 'tint', label: 'Tint', min: -100, max: 100 },
  ] },
  { id: 'light', label: 'Light', icon: 'light', controls: [
    { id: 'brightness', label: 'Brightness', min: -100, max: 100 },
    { id: 'contrast', label: 'Contrast', min: -100, max: 100 },
    { id: 'highlights', label: 'Highlights', min: -100, max: 100 },
    { id: 'shadows', label: 'Shadows', min: -100, max: 100 },
    { id: 'whites', label: 'Whites', min: -100, max: 100 },
    { id: 'blacks', label: 'Blacks', min: -100, max: 100 },
  ] },
  { id: 'colour', label: 'Colour', icon: 'colour', controls: [
    { id: 'vibrance', label: 'Vibrance', min: -100, max: 100 },
    { id: 'saturation', label: 'Saturation', min: -100, max: 100 },
  ] },
  { id: 'texture', label: 'Texture', icon: 'texture', controls: [
    { id: 'sharpness', label: 'Sharpness', min: 0, max: 100 },
    { id: 'clarity', label: 'Clarity', min: -100, max: 100 },
    { id: 'vignette', label: 'Vignette', min: -100, max: 100 },
  ] },
];
export const ADJUST_KEYS = ADJUST_SECTIONS.flatMap(section => section.controls.map(control => control.id));
export type AdjustValues = Record<AdjustKey, number>;
export const NO_ADJUST = Object.fromEntries(ADJUST_KEYS.map(key => [key, 0])) as AdjustValues;

const SKSL = `
uniform shader artworkInput;
uniform float2 u_resolution;
${ADJUST_KEYS.map(key => `uniform float ${key};`).join('\n')}

float3 straight(half4 x) { return x.a > 0.0 ? float3(x.rgb) / float(x.a) : float3(0.0); }
float luma(float3 c) { return dot(c, float3(0.2126, 0.7152, 0.0722)); }

half4 main(float2 p) {
  half4 source = artworkInput.eval(p);
  if (source.a <= 0.0) return source;
  float3 c = straight(source);

  // Texture first: it reads the neighbours as they are.
  if (sharpness > 0.0 || clarity != 0.0) {
    float d = max(1.0, max(u_resolution.x, u_resolution.y) / 1200.0);
    float3 near = (straight(artworkInput.eval(p + float2(d, 0.0))) + straight(artworkInput.eval(p - float2(d, 0.0)))
      + straight(artworkInput.eval(p + float2(0.0, d))) + straight(artworkInput.eval(p - float2(0.0, d)))) * 0.25;
    c += (c - near) * (sharpness / 100.0) * 1.6;
    float D = d * 10.0;
    float3 wide = (straight(artworkInput.eval(p + float2(D, D))) + straight(artworkInput.eval(p - float2(D, D)))
      + straight(artworkInput.eval(p + float2(D, -D))) + straight(artworkInput.eval(p + float2(-D, D)))) * 0.25;
    float mid = 1.0 - abs(luma(c) * 2.0 - 1.0);
    c += (c - wide) * (clarity / 100.0) * 0.7 * mid;
  }

  // White balance: warm/cool along blue-amber, tint along green-magenta.
  float t = temperature / 100.0, n = tint / 100.0;
  c += float3(t * 0.10, -n * 0.08 + t * 0.02, -t * 0.10);

  // Light. Brightness bends the middle and leaves black black and white white, so brightening a
  // dark picture never greys its blacks the way adding light did — nor tints them, as a gamma
  // does by multiplying the faint colour differences right next to black.
  float k = brightness / 100.0 * 0.9;
  c = c + k * c * (1.0 - c);
  c = (c - 0.5) * (1.0 + contrast / 100.0 * 0.7) + 0.5;
  float L = luma(clamp(c, 0.0, 1.0));
  c += (highlights / 100.0) * 0.28 * smoothstep(0.45, 1.0, L);
  c += (shadows / 100.0) * 0.28 * (1.0 - smoothstep(0.0, 0.55, L));
  c += (whites / 100.0) * 0.18 * smoothstep(0.7, 1.0, L);
  c += (blacks / 100.0) * 0.18 * (1.0 - smoothstep(0.0, 0.3, L));

  // Colour: saturation for everything, vibrance mostly for what is not yet colourful.
  float g = luma(c);
  float chroma = max(c.r, max(c.g, c.b)) - min(c.r, min(c.g, c.b));
  c = mix(float3(g), c, 1.0 + saturation / 100.0);
  c = mix(float3(g), c, 1.0 + (vibrance / 100.0) * (1.0 - clamp(chroma, 0.0, 1.0)));

  // Vignette: darker (or, below zero, lighter) toward the corners.
  if (vignette != 0.0) {
    float2 uv = p / max(u_resolution, float2(1.0)) - 0.5;
    float edge = smoothstep(0.15, 0.75, length(uv) * 1.414);
    float k = vignette / 100.0 * edge;
    c = k > 0.0 ? c * (1.0 - k * 0.75) : c + (1.0 - c) * (-k) * 0.6;
  }

  c = clamp(c, 0.0, 1.0);
  return half4(half3(c) * source.a, source.a);
}
`;

export const ADJUST_SHADER: ShaderDef = {
  name: 'Adjust',
  sksl: SKSL,
  usesImage: true,
  controls: ADJUST_SECTIONS.flatMap(section => section.controls.map(control => ({ id: control.id, label: control.label, type: 'number' as const, min: control.min, max: control.max, step: 1, default: 0 }))),
};

type ShaderEffectLike = Extract<Effect, { type: 'shader' }>;
/** Where a layer's Adjust sits in its effects, or −1. */
export function adjustIndex(node: NodeRecord | undefined): number {
  const effects = (node as { effects?: Effect[] } | undefined)?.effects ?? [];
  return effects.findIndex(effect => effect.type === 'shader' && effect.shaderId === ADJUST_SHADER_ID);
}
export function adjustValues(node: NodeRecord | undefined): AdjustValues {
  const at = adjustIndex(node);
  if (at < 0) return { ...NO_ADJUST };
  const effect = ((node as { effects: Effect[] }).effects[at]) as ShaderEffectLike;
  return Object.fromEntries(ADJUST_KEYS.map(key => [key, typeof effect.values[key] === 'number' ? effect.values[key] as number : 0])) as AdjustValues;
}
export const isNeutral = (values: AdjustValues) => ADJUST_KEYS.every(key => !values[key]);
export function adjustEffect(values: AdjustValues): ShaderEffectLike {
  return { type: 'shader', shaderId: ADJUST_SHADER_ID, values: { ...values }, seed: 0, time: 0, source: 'layer', opacity: 1, visible: true };
}

/* ── Auto Adjust ──────────────────────────────────────────────────────────────────────────── */

const smooth = (a: number, b: number, x: number) => { const t = Math.max(0, Math.min(1, (x - a) / (b - a))); return t * t * (3 - 2 * t); };
const lumaOf = (r: number, g: number, b: number) => 0.2126 * r + 0.7152 * g + 0.0722 * b;

/**
 * The shader's light maths on one grey (what Brightness through Blacks do to a tone), the same
 * steps in the same order as `SKSL`. Every light control moves the three channels alike, so the
 * picture's tones can be fitted on its luma alone.
 */
export function toneOf(L: number, v: Pick<AdjustValues, 'brightness' | 'contrast' | 'highlights' | 'shadows' | 'whites' | 'blacks'>): number {
  const k = (v.brightness / 100) * 0.9;
  let c = L + k * L * (1 - L);
  c = (c - 0.5) * (1 + v.contrast / 100 * 0.7) + 0.5;
  const at = Math.max(0, Math.min(1, c));
  c += (v.highlights / 100) * 0.28 * smooth(0.45, 1, at);
  c += (v.shadows / 100) * 0.28 * (1 - smooth(0, 0.55, at));
  c += (v.whites / 100) * 0.18 * smooth(0.7, 1, at);
  c += (v.blacks / 100) * 0.18 * (1 - smooth(0, 0.3, at));
  return Math.max(0, Math.min(1, c));
}

type Tones = Pick<AdjustValues, 'brightness' | 'contrast' | 'highlights' | 'shadows' | 'whites' | 'blacks'>;
const TONE_KEYS = ['brightness', 'contrast', 'highlights', 'shadows', 'whites', 'blacks'] as const;

/**
 * Auto Adjust, from the picture itself (Tarek, 15 Oct: the first one was "shit" — it pulled every
 * picture's average to middle grey, washing out anything moody, neutralised every colour grade and
 * always added vibrance and clarity). This one does what a retoucher's first pass does and no more:
 *
 *  - Tones: the brightest tones reach near-white and the darkest near-black when they fall short,
 *    a flat picture gains contrast, and the middle moves only a little toward middle grey — a dark,
 *    moody picture stays dark. The sliders are FITTED: candidates are tried on the picture's own
 *    tones through the shader's maths (`toneOf`) and the smallest change that reaches the targets
 *    wins, so a well-exposed picture comes back close to untouched.
 *  - Colour: a cast is taken out only where the picture's near-greys clearly carry one, and only
 *    partly; warm or cool grading on colourful content is the photographer's. Vibrance only for a
 *    dull picture; saturation, texture and vignette are left alone.
 *
 * `pixels` is RGBA, read from a small copy of the picture.
 */
export function autoAdjust(pixels: Uint8ClampedArray): AdjustValues {
  const bins = new Float64Array(256);
  const warmth: number[] = [], greenness: number[] = [];
  let n = 0, saturationSum = 0;
  for (let i = 0; i < pixels.length; i += 4) {
    if (pixels[i + 3]! < 16) continue;
    const r = pixels[i]! / 255, g = pixels[i + 1]! / 255, b = pixels[i + 2]! / 255;
    const L = lumaOf(r, g, b), top = Math.max(r, g, b), chroma = top - Math.min(r, g, b);
    bins[Math.min(255, Math.round(L * 255))]! += 1; n++;
    saturationSum += top > 0 ? chroma / top : 0;
    // The middle tones that are only a little coloured: where a cast shows over the whole picture.
    if (chroma < 0.3 && L > 0.15 && L < 0.9) { warmth.push(r - b); greenness.push(g - (r + b) / 2); }
  }
  if (!n) return { ...NO_ADJUST };
  const cumulative = new Float64Array(256);
  for (let i = 0, run = 0; i < 256; i++) { run += bins[i]!; cumulative[i] = run / n; }
  const at = (q: number) => { const i = cumulative.findIndex(c => c >= q); return (i < 0 ? 255 : i) / 255; };
  const lo = at(0.005), q05 = at(0.05), mid = at(0.5), q95 = at(0.95), hi = at(0.995);
  const spread = q95 - q05;

  // Where the tones should end up. A picture with nothing bright in it is underexposed, not moody:
  // its middle comes most of the way up; otherwise the middle moves only a little.
  const under = hi < 0.7;
  const wantHi = hi < 0.94 ? 0.965 : hi;
  const wantLo = lo > 0.05 ? Math.max(0.02, lo * 0.35) : lo;
  const wantMid = Math.max(0.12, Math.min(0.68, mid + (0.46 - mid) * (under ? 0.7 : 0.2)));
  // Blacks may only rise where the shadows are crushed flat; otherwise they only deepen.
  const crushed = cumulative[3]! > 0.03;
  const wantSpread = spread < 0.55 ? Math.min(0.62, spread * 1.25) : spread;
  const cost = (v: Tones) => {
    const H = toneOf(hi, v), Lw = toneOf(lo, v), M = toneOf(mid, v), S = toneOf(q95, v) - toneOf(q05, v);
    let size = 0; for (const key of TONE_KEYS) size += (v[key] / 100) ** 2;
    return 8 * (H - wantHi) ** 2 + 8 * (Lw - wantLo) ** 2 + 5 * (M - wantMid) ** 2 + 3 * (S - wantSpread) ** 2
      // Never clip what was not clipped.
      + 20 * Math.max(0, H - 0.99) ** 2 + 20 * Math.max(0, 0.005 - Lw) ** 2 * Number(lo > 0.01)
      + 0.02 * size;
  };
  const best: Tones = { brightness: 0, contrast: 0, highlights: 0, shadows: 0, whites: 0, blacks: 0 };
  const limit: Record<keyof Tones, number> = { brightness: under ? 100 : 40, contrast: 25, highlights: 30, shadows: 30, whites: 40, blacks: 40 };
  const floor: Record<keyof Tones, number> = { brightness: -limit.brightness, contrast: -20, highlights: -limit.highlights, shadows: -limit.shadows, whites: -20, blacks: -limit.blacks };
  const ceiling: Record<keyof Tones, number> = { ...limit, blacks: crushed ? 15 : 0 };
  let score = cost(best);
  for (const step of [20, 10, 5, 2, 1]) {
    for (let round = 0, moved = true; moved && round < 12; round++) {
      moved = false;
      for (const key of TONE_KEYS) for (const sign of [1, -1]) {
        const tried = { ...best, [key]: Math.max(floor[key], Math.min(ceiling[key], best[key] + sign * step)) };
        const s = cost(tried);
        if (s < score - 1e-9) { Object.assign(best, tried); score = s; moved = true; }
      }
    }
  }

  // Colour. Temperature moves red−blue by 0.2 per 100; tint moves green against the rest by 0.08.
  // The median of the softly coloured middle tones: a cast moves all of them; a red jacket does not.
  // A modest one is half taken out; a strong one is a grade somebody chose, and is left.
  const median = (list: number[]) => { if (!list.length) return 0; const sorted = [...list].sort((a, b) => a - b); return sorted[Math.floor(sorted.length / 2)]!; };
  let temperature = 0, tint = 0;
  if (warmth.length / n > 0.15) {
    const warm = median(warmth), green = median(greenness);
    // Warmth is how most daylight pictures look; only a clear cast is corrected, and a cool one sooner.
    if ((warm > 0.07 || warm < -0.04) && Math.abs(warm) < 0.2) temperature = Math.round(Math.max(-25, Math.min(30, (-warm / 0.2) * 100 * 0.5)));
    if (Math.abs(green) > 0.035 && Math.abs(green) < 0.1) tint = Math.round(Math.max(-20, Math.min(20, ((green + 0.02 * temperature / 100) / 0.08) * 100 * 0.5)));
  }
  // Vibrance only for a colour picture that is dull all over (a black-and-white one has none to raise).
  const saturation = saturationSum / n;
  const vibrance = saturation > 0.02 && saturation < 0.12 ? 12 : 0;
  return { ...NO_ADJUST, ...best, temperature, tint, vibrance };
}
